/**
 * Redux Memoized Selectors (Computing Derived Data)
 *
 * Note: A selector created with createSelector only returns the cached value
 * when its set of arguments is the same as its previous set of arguments.
 *
 * Rule: If a selector will be used on multiple instances of a component, each
 * instance of the component needs its own private copy of the selector.
 * Therefore, we return a function that returns a new copy of the selector each
 * time it is called.
 */

import { createSelector } from 'reselect';
import { get, identity } from 'lodash';

/*******************************************************************************
 * State Selectors
 ******************************************************************************/

const dataSelector = state => state.data;
const bulkSelector = state => state.bulk;
const filterSelector = state => state.filter;
const predicateSelector = (state, predicate) => predicate || identity;

const tagsMetaSelector = state => state.data.meta.tags;
const groupsMetaSelector = state => state.data.meta.groups;
const rolesMetaSelector = state => state.data.meta.roles;
const currentUserSelector = state => state.data.meta.currentUser;
const featureFlagsSelector = state => state.data.meta.features;
const permissionsSelector = state => state.data.meta.permissions;

const globalFeatureFlagsSelector = state => state.meta.features;
const globalTokensSelector = state => state.meta.tokens;

const featureSelector = (state, feature) => feature;
const permissionSelector = (state, permission) => permission;
const tagIdsSelector = (state, ids) => ids;
const itemIndexSelector = (state, index) => index;
const columnNameSelector = (state, name) => name;

/*******************************************************************************
 * Content Page Selectors
 ******************************************************************************/

/**
 * Content Tags by ids (from metadata)
 * @returns {Array} Tags of a content item
 */
export const contentTagsSelector = () =>
  createSelector([tagsMetaSelector, tagIdsSelector], (tagsMeta, tagIds) =>
    tagIds.map(id => _.extend({}, tagsMeta[id], { id })),
  );

/*******************************************************************************
 * People Page Selectors
 ******************************************************************************/

export const userGroupsSelector = () =>
  createSelector(
    [groupsMetaSelector, (state, ids) => ids],
    (groupsMeta, groupIds) =>
      groupIds.map(id => _.extend({}, groupsMeta[id], { id })),
  );

export const userRoleSelector = () =>
  createSelector(
    [rolesMetaSelector, (state, userRoleId) => userRoleId],
    (rolesMeta, userRoleId) =>
      rolesMeta.filter(({ id }) => id === userRoleId)[0],
  );

/*******************************************************************************
 * Bulk Actions Selectors (People & Content)
 ******************************************************************************/

/**
 * All indexes of content that are not practice contaienrs
 * @returns {Array} Indexes [0...n]
 */
export const itemsIndexesSelector = createSelector(
  [dataSelector, predicateSelector],
  ({ content }, predicate) =>
    content.reduce((acc, item, i) => (predicate(item) ? [...acc, i] : acc), []),
);

/**
 * Check if item is selected (in bulk list)
 * @returns {Boolean}
 */
export const isItemSelectedSelector = () =>
  createSelector([bulkSelector, itemIndexSelector], ({ selected }, index) =>
    _.includes(selected, index),
  );

/**
 * Data of selected items in the bulk list
 * @returns {Array} Entire content object
 */
export const selectedItemsSelector = createSelector(
  [dataSelector, bulkSelector],
  ({ content, contentType }, { selected }) =>
    selected.map(index => ({
      id: content[index].id,
      type: content[index].type || contentType,
    })),
);

/**
 * Number of selected items
 * @returns {Number}
 */
export const selectedItemsCountSelector = createSelector(
  [selectedItemsSelector],
  selectedContent => selectedContent.length,
);

/**
 * Check if any content is selected (list is not empty)
 * @returns {Number}
 */
export const hasSelectedItemsSelector = createSelector(
  [selectedItemsCountSelector],
  selectedContentCount => !!selectedContentCount,
);

/**
 * Check if the bulk actions buttons have to be disabled
 */
export const shouldDisableBulkActions = createSelector(
  [hasSelectedItemsSelector, bulkSelector, dataSelector],
  (hasSelected, bulk, data) =>
    !hasSelected || bulk.inProgress || data.isFetching,
);

/*
 * Check if the action bar should be visible
 */
export const shouldShowActionBar = createSelector(
  [hasSelectedItemsSelector, filterSelector],
  (hasSelected, filter) => hasSelected || filter.isActive,
);

/*
 * Check if we should disable content checkboxes
 */
export const shouldDisableContentCheckboxes = createSelector(
  [currentUserSelector, dataSelector],
  (currentUser, data) => {
    if (data.contentType === 'user') {
      return false;
    }
    if (currentUser.canArchiveContent !== undefined) {
      return !currentUser.canArchiveContent;
    }
    return false;
  },
);

/**
 * Check if something went wrong
 */
export const hasError = createSelector(
  [bulkSelector, dataSelector],
  (bulk, data) => [bulk.success, data.success].some(e => e === false),
);

/**
 * Check if something is loading
 */
export const isLoading = createSelector(
  [bulkSelector, dataSelector],
  (bulk, data) =>
    [data.isFetching || bulk.inProgress, !bulk.inProgress && bulk.done].some(
      e => !!e,
    ),
);

/**
 * Get the non archivable items
 */
export const nonArchivableItemsSelector = createSelector(
  [dataSelector, bulkSelector],
  ({ content }, { selected }) =>
    selected
      .map(index => content[index])
      .filter(({ archivable }) => !archivable),
);

/**
 * Get the expert users
 */
export const expertUsersSelector = createSelector(
  [dataSelector, bulkSelector],
  ({ content }, { selected }) =>
    selected
      .map(index => content[index])
      .filter(({ expertCount }) => expertCount > 0),
);

/**
 * Check if the current user is selected
 */
export const hasCurrentUser = createSelector(
  [nonArchivableItemsSelector, currentUserSelector],
  (nonArchivables, { id }) =>
    _.includes(
      nonArchivables.map(item => item.id),
      id,
    ),
);

/**
 * Check if managers/admin have advanced permissions
 */
export const canEditGroups = createSelector(
  [permissionsSelector],
  permissions => permissions.editGroups,
);

/**
 * Check if user can add/edit smart groups
 */
export const hasEditSmartGroupsPermissions = createSelector(
  [permissionsSelector, featureFlagsSelector],
  permissions => permissions.editAllGroups,
);

/**
 * Check if cell is in sorted column and sort direction
 */
export const isSortedColumn = () =>
  createSelector(
    [filterSelector, columnNameSelector],
    (filter, columnName) => filter.sort.column === columnName,
  );

export const sortDirection = () =>
  createSelector(
    [filterSelector, isSortedColumn()],
    ({ sort }, currentColumn) => {
      if (sort.direction && currentColumn) {
        return sort.direction;
      }
      return 'asc';
    },
  );

export const getNewSortingDirection = createSelector(
  [filterSelector, isSortedColumn()],
  ({ sort }, currentColumn) => {
    if (currentColumn && sort.direction === 'asc') {
      return 'desc';
    }
    return 'asc';
  },
);

export const hasGlobalFeature = createSelector(
  [globalFeatureFlagsSelector, featureSelector],
  (features, feature) => get(features, feature, false),
);

export const hasFeature = createSelector(
  [featureFlagsSelector, featureSelector],
  (features, feature) => get(features, feature, false),
);

export const hasPermission = createSelector(
  [permissionsSelector, permissionSelector],
  (permissions, permission) => get(permissions, permission, false),
);

export const getMetaTokens = createSelector(
  [globalTokensSelector],
  tokens => tokens,
);
